import type { AnimationEvent } from '@angular/animations';
import { transition, trigger, useAnimation } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { fadeOut } from '@freelancer/animations';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { ContainerSize } from '@freelancer/ui/container';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import {
  PictureBackgroundImageStyles,
  PictureBgImageObjectFit,
  PictureBgImagePosition,
} from '../picture';
import { BannerAnnouncementType } from './banner-announcement.types';

@Component({
  selector: 'fl-banner-announcement-message',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerAnnouncementMessageComponent {}

@Component({
  selector: 'fl-banner-announcement-buttons',
  template: ` <ng-content select="fl-button"></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerAnnouncementButtonsComponent {}

@Component({
  selector: 'fl-banner-announcement',
  template: `
    <div
      class="AnnouncementBackground"
      [class.IsRoundedCorners]="roundedCorners"
      role="alert"
      [attr.data-type]="type"
      [@hideElement]="state"
      (@hideElement.done)="animationDone($event)"
    >
      <fl-container
        class="AnnouncementContentContainer"
        [class.AnnouncementImageBanner]="isImageBanner"
        [size]="size"
        [mobilePadding]="true"
      >
        <fl-picture
          *ngIf="isImageBanner && imageSrc"
          [backgroundStyles]="backgroundImageStyles"
          [isBackgroundImage]="true"
          [srcDesktop]="imageDesktopSrc"
          [srcTablet]="imageTabletSrc"
          [src]="imageSrc"
        ></fl-picture>
        <div [flMarginRight]="'xsmall'">
          <fl-icon
            [name]="icon"
            [color]="IconColor.LIGHT"
          ></fl-icon>
        </div>
        <div class="AnnouncementWrap">
          <div
            class="AnnouncementContent"
            [flMarginRight]="'large'"
          >
            <p
              class="text-light text-xsmall font-bold"
              *ngIf="bannerTitle"
              [flMarginBottom]="'none'"
            >
              {{ bannerTitle }}
            </p>
            <fl-text
              class="AnnouncementContent-body"
              [size]="TextSize.XSMALL"
              [color]="FontColor.LIGHT"
              [flMarginBottom]="injectedButtons ? 'xsmall' : 'none'"
              [flMarginBottomTablet]="'none'"
            >
              <!-- only inject subcomponents if they're necessary for buttons -->
              <!-- this should make the interface nicer for non-button banners -->
              <ng-container *ngIf="injectedButtons; else allContent">
                <ng-content
                  select="fl-banner-announcement-message"
                ></ng-content>
              </ng-container>
              <ng-template #allContent>
                <ng-content></ng-content>
              </ng-template>
            </fl-text>
          </div>
          <div
            *ngIf="injectedButtons"
            class="AnnouncementButtons"
            [flMarginRight]="'xsmall'"
          >
            <ng-content select="fl-banner-announcement-buttons"></ng-content>
          </div>
        </div>

        <fl-button
          *ngIf="closeable"
          class="AlertCloseButton"
          (click)="handleCloseEvent($event)"
        >
          <fl-icon
            i18n-label="Banner announcement close icon label"
            label="Close banner announcement"
            [name]="'ui-close'"
            [color]="IconColor.LIGHT"
            [size]="IconSize.SMALL"
          ></fl-icon>
        </fl-button>
      </fl-container>
    </div>
  `,
  styleUrls: ['./banner-announcement.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('hideElement', [
      transition('visible => hidden', [
        useAnimation(fadeOut, {
          params: { duration: '500' },
        }),
      ]),
    ]),
  ],
})
export class BannerAnnouncementComponent {
  IconColor = IconColor;
  IconSize = IconSize;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontColor = FontColor;
  TextSize = TextSize;
  FontWeight = FontWeight;
  BannerAnnouncementType = BannerAnnouncementType;
  state = 'visible';
  size: ContainerSize;

  @HostBinding('class.IsHidden') bannerAlertHidden = false;
  @Input() type = BannerAnnouncementType.INFO;
  @Input() bannerTitle?: string;
  @Input() closeable = true;
  @Input() icon: string;
  @Input() isImageBanner = false;
  /** Sets the backgroundStyles to be used when isImageBanner is true */
  @Input() backgroundImageStyles?: PictureBackgroundImageStyles = {
    position: PictureBgImagePosition.CENTER,
    objectFit: PictureBgImageObjectFit.COVER,
  };
  /** Sets the src image for desktop screen sizes when isImageBanner is true */
  @Input() imageDesktopSrc?: string;
  /** Sets the src image for tablet screen sizes when isImageBanner is true */
  @Input() imageTabletSrc?: string;
  /** Sets the src image for mobile screen sizes above when isImageBanner is true */
  @Input() imageSrc?: string;
  @Input() roundedCorners = true;

  @Input() set containerSize(value: ContainerSize) {
    this.size = value || ContainerSize.DESKTOP_LARGE;
  }

  @Output() close = new EventEmitter<void>();

  @ContentChild(BannerAnnouncementButtonsComponent)
  injectedButtons: BannerAnnouncementButtonsComponent;

  animationDone(event: AnimationEvent): void {
    if (event.toState === 'hidden') {
      this.bannerAlertHidden = true;
    }
  }

  handleCloseEvent(event: MouseEvent): void {
    // Stops event bubble up to parent elements (i.e. link components to redirect)
    event.preventDefault();
    event.stopPropagation();

    this.state = 'hidden';
    this.close.emit();
  }
}
